import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider, Theme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

interface RTLContextType {
  direction: 'rtl' | 'ltr';
  language: string;
  setLanguage: (lang: string) => void;
  toggleDirection: () => void;
}

const RTLContext = createContext<RTLContextType | undefined>(undefined);

export const useRTL = () => {
  const context = useContext(RTLContext);
  if (!context) {
    throw new Error('useRTL must be used within RTLProvider');
  }
  return context;
};

// RTL languages that Tibbbi might support
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

// Create RTL cache for emotion
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create LTR cache for emotion
const cacheLtr = createCache({
  key: 'mui',
  stylisPlugins: [prefixer],
});

interface RTLProviderProps {
  children: ReactNode;
}

export const RTLProvider: React.FC<RTLProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<string>(() => {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam) return langParam;
    
    // Check localStorage
    const savedLang = localStorage.getItem('language');
    if (savedLang) return savedLang;
    
    // Check browser language
    const browserLang = navigator.language.split('-')[0];
    return browserLang || 'en';
  });

  const [direction, setDirection] = useState<'rtl' | 'ltr'>(() => {
    const lang = language.split('-')[0];
    return RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
  });

  useEffect(() => {
    // Update direction based on language
    const lang = language.split('-')[0];
    const newDirection = RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
    setDirection(newDirection);
    
    // Update document attributes
    document.documentElement.setAttribute('dir', newDirection);
    document.documentElement.setAttribute('lang', language);
    
    // Save to localStorage
    localStorage.setItem('language', language);
  }, [language]);

  const toggleDirection = () => {
    const newDirection = direction === 'rtl' ? 'ltr' : 'rtl';
    setDirection(newDirection);
    document.documentElement.setAttribute('dir', newDirection);
  };

  // Create theme with RTL support
  const theme = React.useMemo(
    () =>
      createTheme({
        direction: direction,
        palette: {
          mode: 'light',
          primary: {
            main: '#1976d2',
          },
          secondary: {
            main: '#dc004e',
          },
        },
        typography: {
          fontFamily: direction === 'rtl' 
            ? '"IBM Plex Sans Arabic", "Segoe UI", "Roboto", "Arial", sans-serif'
            : '"Roboto", "Helvetica", "Arial", sans-serif',
        },
        components: {
          MuiTextField: {
            defaultProps: {
              dir: direction,
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
              },
            },
          },
        },
      }),
    [direction]
  );

  const cache = direction === 'rtl' ? cacheRtl : cacheLtr;

  return (
    <RTLContext.Provider 
      value={{ 
        direction, 
        language, 
        setLanguage, 
        toggleDirection 
      }}
    >
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </RTLContext.Provider>
  );
};